import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, CircularProgress } from '@mui/material';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert } from '../../../../../../shared/lib/common/Alert';
import { CustomController } from '../../../../../../shared/lib/common/CustomController';
import { PageHeading } from '../../../../../../shared/lib/common/PageHeading';
import { BUTTON_TEXT_RESET_PASSWORD, PAGE_TITLE_RESET_PASSWORD } from '../../../../../../shared/lib/constants';
import palette from '../../../../../../shared/lib/theme/palette';
import { flexCenter } from '../../../../../../shared/lib/theme/styleConstants';
import { AuthContainer } from '../../../../components/auth/AuthContainer';
import { useVerifyResetPasswordTokenAndSetPasswordMutation } from '../../../../generated';
import { LINKS, MSG_PASSWORD_RESET_SUCCESSFULLY, RESET_PASSWORD_FIELDS } from '../../../constants';
import { ResetPasswordData } from '../../../interfaceTypes';
import { resetPasswordSchema } from '../../../validationSchema';
import { useMobileView } from '../../../hooks/useMobileView';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const userToken = searchParams.get('token');
  const navigate = useNavigate();
  const { blackOne } = palette;

  const methods = useForm<ResetPasswordData, any, ResetPasswordData>({
    mode: 'all',
    resolver: yupResolver(resetPasswordSchema),

    defaultValues: {
      newPassword: '',
      repeatPassword: ''
    }
  });
  const { handleSubmit } = methods;

  const [resetPassword, { loading }] = useVerifyResetPasswordTokenAndSetPasswordMutation({
    onCompleted() {
      Alert.success(MSG_PASSWORD_RESET_SUCCESSFULLY);
      navigate(LINKS.LOGIN_LINK);
    },
    onError({ message }) {
      Alert.error(message);
    }
  });

  const onSubmit: SubmitHandler<ResetPasswordData> = async (data) => {
    const { newPassword } = data;

    if (userToken) {
      await resetPassword({
        variables: {
          resetPasswordInput: {
            password: newPassword,
            token: userToken
          }
        }
      });
    }
  };

  const mediumViewport = useMobileView();

  return (
    <AuthContainer>
      <Box flex={mediumViewport ? 'none' : 1} sx={{ ...flexCenter, flexDirection: 'column' }}>
        <Box width={mediumViewport ? '100%' : '500px'} paddingX="20px" textAlign="center">
          <PageHeading color={blackOne} title={PAGE_TITLE_RESET_PASSWORD} marginBottom="2rem" />

          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {RESET_PASSWORD_FIELDS.map((field, index) => {
                const { fieldType, name, title } = field;

                return <CustomController key={`${index}-${name}`} controllerName={name} controllerLabel={title} fieldType={fieldType} />;
              })}

              <Button variant="contained" sx={{ mt: 2 }} type="submit" disabled={loading} endIcon={loading && <CircularProgress size={20} color="inherit" />} fullWidth>
                {BUTTON_TEXT_RESET_PASSWORD}
              </Button>
            </form>
          </FormProvider>
        </Box>
      </Box>
    </AuthContainer>
  );
};

export default ResetPassword;
